import { useState, useEffect, useCallback } from "react"
import { X, ChevronLeft, ChevronRight } from "lucide-react"
import { cn } from "@/lib/utils"

export interface GalleryPhoto {
    url: string
    altText?: string | null
}

interface PhotoLightboxProps {
    photos: GalleryPhoto[]
    initialIndex: number
    onClose: () => void
}

const PhotoLightbox = ({ photos, initialIndex, onClose }: PhotoLightboxProps) => {
    const [index, setIndex] = useState(initialIndex)
    const current = photos[index]

    const prev = useCallback(() => setIndex((i) => (i - 1 + photos.length) % photos.length), [photos.length])
    const next = useCallback(() => setIndex((i) => (i + 1) % photos.length), [photos.length])

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose()
            if (e.key === "ArrowLeft") prev()
            if (e.key === "ArrowRight") next()
        }
        window.addEventListener("keydown", handler)
        return () => window.removeEventListener("keydown", handler)
    }, [onClose, prev, next])

    if (!current) return null

    return (
        <div
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
            onClick={onClose}
        >
            {/* Close */}
            <button
                className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors p-2"
                onClick={onClose}
                aria-label="Close"
            >
                <X className="size-6" />
            </button>

            {/* Counter */}
            {photos.length > 1 && (
                <span className="absolute top-4 left-1/2 -translate-x-1/2 text-white/60 text-sm font-bold">
                    {index + 1} / {photos.length}
                </span>
            )}

            {/* Prev */}
            {photos.length > 1 && (
                <button
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors p-2"
                    onClick={(e) => { e.stopPropagation(); prev() }}
                    aria-label="Previous"
                >
                    <ChevronLeft className="size-8" />
                </button>
            )}

            {/* Image */}
            <img
                src={current.url}
                alt={current.altText ?? ""}
                className="max-h-[90vh] max-w-[90vw] object-contain rounded-2xl shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            />

            {/* Next */}
            {photos.length > 1 && (
                <button
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors p-2"
                    onClick={(e) => { e.stopPropagation(); next() }}
                    aria-label="Next"
                >
                    <ChevronRight className="size-8" />
                </button>
            )}
        </div>
    )
}

interface PhotoGalleryProps {
    photos: GalleryPhoto[]
    /** Width of each thumbnail. Default: "w-48" */
    thumbWidth?: string
    /** Height of each thumbnail. Default: "h-32" */
    thumbHeight?: string
    className?: string
}

export const PhotoGallery = ({ photos, thumbWidth = "w-48", thumbHeight = "h-32", className }: PhotoGalleryProps) => {
    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

    if (photos.length === 0) return null

    return (
        <>
            <div className={cn("flex gap-3 overflow-x-auto pb-2", className)}>
                {photos.map((photo, i) => (
                    <button
                        key={photo.url + i}
                        type="button"
                        onClick={() => setLightboxIndex(i)}
                        className={cn(
                            "shrink-0 rounded-2xl overflow-hidden shadow-md cursor-pointer",
                            "hover:scale-[1.03] transition-transform duration-200 active:scale-100",
                            thumbWidth,
                            thumbHeight,
                        )}
                    >
                        <img
                            src={photo.url}
                            alt={photo.altText ?? ""}
                            className="w-full h-full object-cover"
                        />
                    </button>
                ))}
            </div>

            {lightboxIndex !== null && (
                <PhotoLightbox
                    photos={photos}
                    initialIndex={lightboxIndex}
                    onClose={() => setLightboxIndex(null)}
                />
            )}
        </>
    )
}
